import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, Volume2, X } from "lucide-react";
import { API_ORIGIN } from "../api";
import { registerPushNotifications, listenForForegroundMessages } from "../pushNotifications";

const sound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    if (ctx.state === "suspended") ctx.resume();
    [0, 0.16].forEach((delay) => {
      const tone = ctx.createOscillator();
      const gain = ctx.createGain();
      tone.type = "sine";
      tone.frequency.value = delay ? 880 : 660;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + delay + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.20);
      tone.connect(gain).connect(ctx.destination);
      tone.start(ctx.currentTime + delay);
      tone.stop(ctx.currentTime + delay + 0.21);
    });
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch { /* sound needs a browser user gesture to be enabled */ }
};

export default function DesktopNotifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [permission, setPermission] = useState(typeof Notification === "undefined" ? "unsupported" : Notification.permission);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("agpSoundEnabled") === "true");
  const seen = useRef(new Map());
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const staff = ["admin", "worker"].includes(user?.role);
  const target = staff ? "/admin/notifications" : "/notifications";

  useEffect(() => {
    if (!token) return;
    let active = true;
    const show = (n) => {
      if (!active || !n) return;
      const key = n._id || `${n.title || n.notification?.title}:${n.message || n.notification?.body}`;
      const now = Date.now();
      if (seen.current.has(key) && now - seen.current.get(key) < 15000) return;
      seen.current.set(key, now);
      if (seen.current.size > 100) seen.current.clear();
      const item = { title: n.title || n.notification?.title || "All Good Parts", message: n.message || n.notification?.body || "New update", link: n.link || target };
      setToast(item);
      if (soundEnabled) sound();
      if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.visibilityState !== "visible") {
        try { const notification = new Notification(item.title, { body: item.message, tag: key }); notification.onclick = () => { window.focus(); navigate(item.link.startsWith("/") ? item.link : target); notification.close(); }; } catch {}
      }
    };
    const socket = io(API_ORIGIN, { transports: ["websocket", "polling"], auth: { token } });
    socket.on("notification:new", show);
    let unsubscribe = () => {};
    listenForForegroundMessages((payload) => show({ ...payload?.data, title: payload?.notification?.title || payload?.data?.title, message: payload?.notification?.body || payload?.data?.body })).then((fn) => { if (active) unsubscribe = fn; else fn?.(); });
    registerPushNotifications({ requestPermission: false });
    return () => { active = false; socket.disconnect(); unsubscribe(); };
  }, [token, soundEnabled, target, navigate]);

  const enable = async () => {
    localStorage.setItem("agpSoundEnabled", "true");
    setSoundEnabled(true);
    sound();
    if ("Notification" in window) {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === "granted") await registerPushNotifications({ requestPermission: false });
    }
  };
  if (!token) return null;
  return <>
    {!soundEnabled ? <button type="button" onClick={enable} className="fixed bottom-4 left-4 z-[9998] flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-xs font-semibold text-white shadow-lg"><Volume2 size={16}/>Enable desktop alerts &amp; sound</button> : null}
    {toast && <div role="alert" className="fixed bottom-5 right-5 z-[9999] w-[min(350px,calc(100vw-32px))] rounded-xl border border-slate-200 bg-white p-4 shadow-2xl">
      <div className="flex items-start gap-3"><Bell className="text-red-600 shrink-0" size={20}/><div className="flex-1 min-w-0"><strong className="block text-sm text-slate-900">{toast.title}</strong><p className="mt-1 text-sm text-slate-600">{toast.message}</p><button className="mt-2 text-xs font-semibold text-red-700 underline" onClick={() => { setToast(null); navigate(toast.link.startsWith("/") ? toast.link : target); }}>View notification</button></div><button aria-label="Dismiss notification" onClick={() => setToast(null)}><X size={16}/></button></div>
    </div>}
  </>;
}
